/**
 * Logger Service
 *
 * Centralized logging system for tracking errors, warnings, and API calls
 * Supports exporting logs for debugging
 */

import { API_CONFIG, TIME } from '../constants';

export interface LogEntry {
  timestamp: number;
  level: 'info' | 'warn' | 'error';
  category: string;
  message: string;
  details?: any;
  stack?: string;
}

export interface ServiceStatus {
  name: string;
  status: 'available' | 'unavailable' | 'degraded' | 'unknown';
  lastCheck: number;
  error?: string;
}

class Logger {
  private logs: LogEntry[] = [];
  private maxLogs = API_CONFIG.LOG_MAX_ENTRIES; // Keep last N logs
  private serviceStatuses: Map<string, ServiceStatus> = new Map();

  /**
   * Log an info message
   */
  info(category: string, message: string, details?: any) {
    this.addLog('info', category, message, details);
  }

  /**
   * Log a warning
   */
  warn(category: string, message: string, details?: any) {
    this.addLog('warn', category, message, details);
    console.warn(`[${category}] ${message}`, details);
  }

  /**
   * Log an error
   */
  error(category: string, message: string, error?: any) {
    const stack = error?.stack || new Error().stack;
    this.addLog('error', category, message, error, stack);
    console.error(`[${category}] ${message}`, error);
  }

  /**
   * Add log entry
   */
  private addLog(level: LogEntry['level'], category: string, message: string, details?: any, stack?: string) {
    const entry: LogEntry = {
      timestamp: Date.now(),
      level,
      category,
      message,
      details,
      stack
    };

    this.logs.push(entry);

    // ✅ Remove logs older than 24 hours (prevents memory leak)
    const maxAge = Date.now() - API_CONFIG.LOG_MAX_AGE_MS;
    this.logs = this.logs.filter(log => log.timestamp > maxAge);

    // Also enforce max count as backup
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }
  }

  /**
   * Update service status
   */
  updateServiceStatus(serviceName: string, status: ServiceStatus['status'], error?: string) {
    this.serviceStatuses.set(serviceName, {
      name: serviceName,
      status,
      lastCheck: Date.now(),
      error
    });
  }

  /**
   * Get all logs
   */
  getLogs(level?: LogEntry['level']): LogEntry[] {
    if (level) {
      return this.logs.filter(log => log.level === level);
    }
    return [...this.logs];
  }

  /**
   * Get service statuses
   */
  getServiceStatuses(): ServiceStatus[] {
    return Array.from(this.serviceStatuses.values());
  }

  /**
   * Clear all logs
   */
  clearLogs() {
    this.logs = [];
  }

  /**
   * Export logs as JSON
   */
  exportLogsAsJSON(): string {
    const exportData = {
      exportDate: new Date().toISOString(),
      totalLogs: this.logs.length,
      services: this.getServiceStatuses(),
      logs: this.logs,
      summary: {
        errors: this.logs.filter(l => l.level === 'error').length,
        warnings: this.logs.filter(l => l.level === 'warn').length,
        info: this.logs.filter(l => l.level === 'info').length
      }
    };

    return JSON.stringify(exportData, null, 2);
  }

  /**
   * Export logs as formatted text
   */
  exportLogsAsText(): string {
    const services = this.getServiceStatuses();
    const errors = this.logs.filter(l => l.level === 'error');
    const warnings = this.logs.filter(l => l.level === 'warn');

    let output = '='.repeat(80) + '\n';
    output += 'AI TRADING BOT - DEBUG LOGS\n';
    output += '='.repeat(80) + '\n\n';
    output += `Export Date: ${new Date().toISOString()}\n`;
    output += `Total Logs: ${this.logs.length}\n`;
    output += `Errors: ${errors.length} | Warnings: ${warnings.length}\n\n`;

    // Service Status
    output += '-'.repeat(80) + '\n';
    output += 'SERVICE STATUS\n';
    output += '-'.repeat(80) + '\n';
    services.forEach(service => {
      const statusIcon = service.status === 'available' ? '✅' :
                        service.status === 'degraded' ? '⚠️' :
                        service.status === 'unavailable' ? '❌' : '❓';
      output += `${statusIcon} ${service.name}: ${service.status.toUpperCase()}\n`;
      if (service.error) {
        output += `   Error: ${service.error}\n`;
      }
      output += `   Last checked: ${new Date(service.lastCheck).toLocaleString()}\n\n`;
    });

    // Errors
    if (errors.length > 0) {
      output += '\n' + '-'.repeat(80) + '\n';
      output += 'ERRORS\n';
      output += '-'.repeat(80) + '\n';
      errors.forEach((log, i) => {
        output += `\n[${i + 1}] ${new Date(log.timestamp).toLocaleString()}\n`;
        output += `Category: ${log.category}\n`;
        output += `Message: ${log.message}\n`;
        if (log.details) {
          output += `Details: ${JSON.stringify(log.details, null, 2)}\n`;
        }
        if (log.stack) {
          output += `Stack:\n${log.stack}\n`;
        }
      });
    }

    // Warnings
    if (warnings.length > 0) {
      output += '\n' + '-'.repeat(80) + '\n';
      output += 'WARNINGS\n';
      output += '-'.repeat(80) + '\n';
      warnings.forEach((log, i) => {
        output += `\n[${i + 1}] ${new Date(log.timestamp).toLocaleString()}\n`;
        output += `Category: ${log.category}\n`;
        output += `Message: ${log.message}\n`;
        if (log.details) {
          output += `Details: ${JSON.stringify(log.details, null, 2)}\n`;
        }
      });
    }

    // All Logs (last 50)
    output += '\n' + '-'.repeat(80) + '\n';
    output += 'RECENT LOGS (Last 50)\n';
    output += '-'.repeat(80) + '\n';
    const recentLogs = this.logs.slice(-50);
    recentLogs.forEach(log => {
      const icon = log.level === 'error' ? '❌' : log.level === 'warn' ? '⚠️' : 'ℹ️';
      output += `${icon} [${new Date(log.timestamp).toLocaleTimeString()}] [${log.category}] ${log.message}\n`;
    });

    output += '\n' + '='.repeat(80) + '\n';
    output += 'END OF LOGS\n';
    output += '='.repeat(80) + '\n';

    return output;
  }

  /**
   * Download logs as file
   */
  downloadLogs(format: 'json' | 'txt' = 'txt') {
    const content = format === 'json' ? this.exportLogsAsJSON() : this.exportLogsAsText();
    const filename = `trading-bot-logs-${new Date().toISOString().split('T')[0]}.${format}`;
    const blob = new Blob([content], { type: format === 'json' ? 'application/json' : 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }
}

// Global logger instance
export const logger = new Logger();
